import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Shield, Search, Circle, MessageSquare, ChevronRight, AlertTriangle, Bell } from "lucide-react";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [inactive, setInactive] = useState([]);

  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => {
      toast.error(e?.response?.data?.detail || "Errore caricamento utenti");
    }).finally(() => setLoading(false));
    api.get("/admin/inactive-clients?days=7").then((r) => setInactive(r.data)).catch(() => {});
  }, []);

  const filtered = users.filter((u) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s);
  });

  const totalActive = users.filter((u) => u.status !== "suspended").length;
  const totalSessions = users.reduce((a, u) => a + (u.total_sessions || 0), 0);
  const totalUnread = users.reduce((a, u) => a + (u.unread_from_admin || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#E2FF31] mb-2">
        <Shield className="w-3 h-3" /> Console amministratore
      </div>
      <h1 className="font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter leading-[0.9] mb-8">
        Clienti <span className="text-[#FF3B30]">& gestione</span>
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 stagger">
        <Stat label="Clienti totali" value={users.length} accent="#FF3B30" />
        <Stat label="Attivi" value={totalActive} accent="#E2FF31" />
        <Stat label="Sessioni totali" value={totalSessions} accent="#FFFFFF" />
        <Stat label="Non letti" value={totalUnread} accent="#A0A0AB" />
      </div>

      {/* Dashboard Riprendi — clienti inattivi da 7+ giorni */}
      {inactive.length > 0 && (
        <div className="border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-5 mb-8" data-testid="riprendi-widget">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-[#F59E0B]" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#F59E0B]">Dashboard riprendi · clienti da rimotivare</div>
          </div>
          <div className="text-sm text-[#A0A0AB] mb-4">
            {inactive.length} {inactive.length === 1 ? "cliente" : "clienti"} inattivi da 7+ giorni. Un messaggio o email di incoraggiamento fa la differenza.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {inactive.slice(0, 6).map((u) => (
              <Link
                key={u.id}
                to={`/admin/users/${u.id}?tab=messages`}
                data-testid={`inactive-${u.id}`}
                className="border border-[#2A2A35] hover:border-[#F59E0B] p-3 flex items-center justify-between gap-2 transition-all"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-bold truncate text-sm">{u.name}</div>
                  <div className="text-xs text-[#A0A0AB]">
                    {u.days_since_last == null ? (
                      <span className="text-[#EF4444]">Mai allenato</span>
                    ) : (
                      <span>Fermo da <b className="text-[#F59E0B]">{u.days_since_last}gg</b></span>
                    )}
                  </div>
                </div>
                <Bell className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />
              </Link>
            ))}
          </div>
          {inactive.length > 6 && (
            <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB] mt-3">+ altri {inactive.length - 6} in fondo alla lista</div>
          )}
        </div>
      )}

      <div className="mb-4 relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A0AB]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca per nome o email..."
          data-testid="admin-search"
          className="w-full bg-[#141419] border border-[#2A2A35] pl-10 pr-4 py-3 focus:border-[#FF3B30] focus:outline-none"
        />
      </div>

      {loading ? (
        <div className="text-[#A0A0AB] font-display uppercase tracking-widest text-sm">Caricamento...</div>
      ) : filtered.length === 0 ? (
        <div className="border border-[#2A2A35] p-8 text-center text-[#A0A0AB]">Nessun cliente.</div>
      ) : (
        <div className="border border-[#2A2A35] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-[#2A2A35] bg-[#141419]/50">
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#A0A0AB]">
                <th className="p-3">Cliente</th>
                <th className="p-3 hidden md:table-cell">Stato</th>
                <th className="p-3 hidden lg:table-cell">Peso</th>
                <th className="p-3 hidden lg:table-cell">Programma</th>
                <th className="p-3">Sessioni</th>
                <th className="p-3 hidden md:table-cell">Ultima attività</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2A2A35]">
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`admin-row-${u.id}`} className="hover:bg-[#141419]/40 transition-colors">
                  <td className="p-3">
                    <div className="font-bold flex items-center gap-2">
                      {u.name}
                      {u.role === "admin" && <span className="text-[9px] uppercase tracking-widest text-[#E2FF31] border border-[#E2FF31] px-1.5 py-0.5">ADMIN</span>}
                      {u.unread_from_admin > 0 && (
                        <span className="text-[9px] bg-[#FF3B30] text-white px-1.5 py-0.5 uppercase tracking-widest">
                          {u.unread_from_admin} nuovi
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#A0A0AB]">{u.email}</div>
                  </td>
                  <td className="p-3 hidden md:table-cell">
                    <span className={`text-[10px] uppercase tracking-widest flex items-center gap-1 ${
                      u.status === "suspended" ? "text-[#EF4444]" : "text-[#22C55E]"
                    }`}>
                      <Circle className="w-2 h-2 fill-current" />
                      {u.status === "suspended" ? "SOSPESO" : "ATTIVO"}
                    </span>
                  </td>
                  <td className="p-3 hidden lg:table-cell font-mono-alt text-xs">
                    {u.current_weight_kg ? (
                      <>{u.current_weight_kg}kg{u.weight_goal_kg && <span className="text-[#A0A0AB]"> → {u.weight_goal_kg}kg</span>}</>
                    ) : <span className="text-[#A0A0AB]">—</span>}
                  </td>
                  <td className="p-3 hidden lg:table-cell text-xs">
                    {u.active_program ? (
                      <span className="text-[#E2FF31]">{u.active_program.title}</span>
                    ) : <span className="text-[#A0A0AB]">nessuno</span>}
                  </td>
                  <td className="p-3 font-mono-alt">{u.total_sessions}</td>
                  <td className="p-3 hidden md:table-cell text-xs text-[#A0A0AB]">
                    {u.last_activity ? new Date(u.last_activity).toLocaleDateString("it-IT") : "—"}
                  </td>
                  <td className="p-3 text-right">
                    <Link
                      to={`/admin/users/${u.id}`}
                      data-testid={`admin-open-${u.id}`}
                      className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest hover:text-[#FF3B30]"
                    >
                      Apri <ChevronRight className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="border border-[#2A2A35] p-5">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-3">{label}</div>
      <div className="font-display text-5xl font-black tracking-tighter" style={{ color: accent }}>{value}</div>
    </div>
  );
}
